import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { CardSection } from 'foundations-components/transitional/components/Card';
import styled from 'foundations-theming/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { breakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { CTA_WIDTH_DEFAULT, CTA_WIDTH_WIDE, hasExtraColumns, LABEL_FLEX_COMPACT, LABEL_FLEX_DEFAULT, SEARCH_FLEX_COMPACT, SEARCH_FLEX_DEFAULT } from './featureTableColumnLayout';

/* Representative body size. The real table is expanded by default and its
 * category/row counts come from the server, so we can't match its height
 * exactly; these approximate a typical (tall) HSOC table so the load-to-content
 * jump (CLS) stays small. Tune after visual QA if the reserve looks off. */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const SKELETON_CATEGORY_COUNT = 8;
export const SKELETON_ROWS_PER_CATEGORY = 6;
/* The synthetic "Plan Details" group always leads the body with exactly these
 * three rows (starting price, user limits, included credits). */
export const PLAN_DETAILS_ROW_COUNT = 3;

/* HSOC v2 shows up to four tier columns (FREE/STARTER/PROFESSIONAL/ENTERPRISE);
 * used as the skeleton's column count before the real tiers have loaded. */
export const DEFAULT_SKELETON_COLUMN_COUNT = 4;
const SHIMMER_ANIMATION = 'hsoc-feature-table-skeleton-shimmer';

/*
 * A single shimmer bar that reserves its own height. Unlike the shared
 * SingleSkeletonLoader (which sets `display:none` under prefers-reduced-motion
 * and so collapses the space the skeleton is meant to reserve), this keeps its
 * box and only drops the animation for reduced-motion users. Purely decorative:
 * the skeleton root owns the single loading status, so the bars carry no role.
 */
const SkeletonBar = styled.div.withConfig({
  displayName: "HsocFeatureTableSkeleton__SkeletonBar"
})(({
  theme,
  $height,
  $width,
  $circle
}) => ({
  height: typeof $height === 'number' ? `${$height}px` : $height,
  width: typeof $width === 'number' ? `${$width}px` : $width,
  flexShrink: 0,
  borderRadius: $circle ? '50%' : '0.25rem',
  overflow: 'hidden',
  position: 'relative',
  userSelect: 'none',
  background: theme.color['fill-tertiary-hover'],
  backgroundImage: `linear-gradient(
      to right,
      ${theme.color['fill-tertiary-hover']},
      ${theme.color['fill-tertiary-default']} 20%,
      ${theme.color['fill-tertiary-hover']} 40%
    )`,
  backgroundSize: '200%',
  backgroundRepeat: 'no-repeat',
  animation: `${SHIMMER_ANIMATION} 2s linear infinite`,
  [`@keyframes ${SHIMMER_ANIMATION}`]: {
    '0%': {
      backgroundPositionX: '100%'
    },
    '100%': {
      backgroundPositionX: '-100%'
    }
  },
  '@media (prefers-reduced-motion)': {
    animation: 'none'
  }
}));

/* CSS-only desktop/mobile swap (matches HsocFeatureTable) so the skeleton shows
 * the layout the user will actually get without a JS-driven flash. */
const SkeletonDesktopLayout = styled.div.withConfig({
  displayName: "HsocFeatureTableSkeleton__SkeletonDesktopLayout"
})({
  [breakpoint.tablet]: {
    display: 'none'
  }
});
const SkeletonMobileLayout = styled.div.withConfig({
  displayName: "HsocFeatureTableSkeleton__SkeletonMobileLayout"
})({
  display: 'none',
  [breakpoint.tablet]: {
    display: 'block'
  }
});
const Title = () => /*#__PURE__*/_jsx(View, {
  paddingBlockStart: "1000",
  paddingInline: "1000",
  children: /*#__PURE__*/_jsx(Flex, {
    justifyContent: "center",
    paddingBlockStart: "700",
    paddingBlockEnd: "600",
    children: /*#__PURE__*/_jsx(SkeletonBar, {
      $height: 32,
      $width: 280
    })
  })
});

/* Mirrors FeatureTableHeader's sticky columns row: a search field followed by
 * one tier column (badge slot + label + CTA) per column. */
const DesktopHeader = ({
  columnCount
}) => {
  const extraColumns = hasExtraColumns(columnCount);
  const searchFlex = extraColumns ? SEARCH_FLEX_COMPACT : SEARCH_FLEX_DEFAULT;
  const ctaWidth = extraColumns ? CTA_WIDTH_WIDE : CTA_WIDTH_DEFAULT;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Title, {}), /*#__PURE__*/_jsx(View, {
      paddingInline: "1000",
      paddingBlockEnd: "600",
      children: /*#__PURE__*/_jsxs(Flex, {
        paddingBlockStart: "300",
        children: [/*#__PURE__*/_jsx(Flex, {
          flex: searchFlex,
          paddingInlineEnd: "200",
          flexDirection: "column",
          justifyContent: "end",
          children: /*#__PURE__*/_jsx(SkeletonBar, {
            $height: 40,
            $width: "100%"
          })
        }), Array.from({
          length: columnCount
        }, (_, index) => /*#__PURE__*/_jsxs(Flex, {
          "data-selenium-id": "hsoc-feature-table-skeleton-column",
          flexDirection: "column",
          alignItems: "center",
          gap: "200",
          flex: "1",
          paddingInline: "200",
          children: [/*#__PURE__*/_jsx(View, {
            blockSize: "24px"
          }), /*#__PURE__*/_jsx(SkeletonBar, {
            $height: 20,
            $width: "70%"
          }), /*#__PURE__*/_jsx(View, {
            paddingBlockStart: "300",
            inlineSize: ctaWidth,
            children: /*#__PURE__*/_jsx(SkeletonBar, {
              $height: 40,
              $width: "100%"
            })
          })]
        }, index))]
      })
    })]
  });
};
const InclusionCell = () => /*#__PURE__*/_jsx(Flex, {
  flex: "1",
  justifyContent: "center",
  alignItems: "center",
  children: /*#__PURE__*/_jsx(SkeletonBar, {
    $height: 20,
    $width: 20,
    $circle: true
  })
});

/* One expanded feature row: a left-hand label + one inclusion mark per column,
 * matching FeatureRowItem. */
const DesktopFeatureRow = ({
  columnCount,
  labelFlex
}) => /*#__PURE__*/_jsx(View, {
  borderBlockStartStyle: "solid",
  borderWidth: "100",
  borderColor: "border-core-default",
  children: /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    paddingBlock: "400",
    children: [/*#__PURE__*/_jsx(Flex, {
      flex: labelFlex,
      paddingInlineStart: "300",
      children: /*#__PURE__*/_jsx(SkeletonBar, {
        $height: 16,
        $width: "80%"
      })
    }), Array.from({
      length: columnCount
    }, (_, index) => /*#__PURE__*/_jsx(InclusionCell, {}, index))]
  })
});

/* One expanded category (FeatureTableCategory): a title/toggle row followed by
 * `rowCount` feature rows. Used for both the Plan Details group and each feature
 * category. */
const DesktopCategory = ({
  columnCount,
  labelFlex,
  rowCount,
  seleniumId
}) => /*#__PURE__*/_jsxs(View, {
  "data-selenium-id": seleniumId,
  borderBlockStartStyle: "solid",
  borderWidth: "200",
  borderColor: "border-core-default",
  children: [/*#__PURE__*/_jsx(View, {
    paddingBlock: "400",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      paddingInline: "200",
      children: [/*#__PURE__*/_jsx(SkeletonBar, {
        $height: 20,
        $width: 180
      }), /*#__PURE__*/_jsx(SkeletonBar, {
        $height: 20,
        $width: 20
      })]
    })
  }), Array.from({
    length: rowCount
  }, (_, index) => /*#__PURE__*/_jsx(DesktopFeatureRow, {
    columnCount: columnCount,
    labelFlex: labelFlex
  }, index))]
});
const DesktopSkeleton = ({
  columnCount
}) => {
  const labelFlex = hasExtraColumns(columnCount) ? LABEL_FLEX_COMPACT : LABEL_FLEX_DEFAULT;
  return /*#__PURE__*/_jsxs(SkeletonDesktopLayout, {
    "data-selenium-id": "hsoc-feature-table-skeleton-desktop",
    children: [/*#__PURE__*/_jsx(DesktopHeader, {
      columnCount: columnCount
    }), /*#__PURE__*/_jsxs(CardSection, {
      paddingBlockStart: "0",
      children: [/*#__PURE__*/_jsxs(View, {
        borderBlockEndStyle: "solid",
        borderWidth: "200",
        borderColor: "border-core-default",
        children: [/*#__PURE__*/_jsx(DesktopCategory, {
          columnCount: columnCount,
          labelFlex: labelFlex,
          rowCount: PLAN_DETAILS_ROW_COUNT,
          seleniumId: "hsoc-feature-table-skeleton-plan-details"
        }), Array.from({
          length: SKELETON_CATEGORY_COUNT
        }, (_, index) => /*#__PURE__*/_jsx(DesktopCategory, {
          columnCount: columnCount,
          labelFlex: labelFlex,
          rowCount: SKELETON_ROWS_PER_CATEGORY,
          seleniumId: "hsoc-feature-table-skeleton-category"
        }, index))]
      }), /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          children: /*#__PURE__*/_jsx(SkeletonBar, {
            $height: 16,
            $width: 220
          })
        })
      })]
    })]
  });
};
const MobileFeatureRow = () => /*#__PURE__*/_jsx(View, {
  borderBlockStartStyle: "solid",
  borderWidth: "100",
  borderColor: "border-core-default",
  children: /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "space-between",
    alignItems: "center",
    paddingBlock: "300",
    paddingInline: "300",
    children: [/*#__PURE__*/_jsx(SkeletonBar, {
      $height: 16,
      $width: "60%"
    }), /*#__PURE__*/_jsx(SkeletonBar, {
      $height: 20,
      $width: 20,
      $circle: true
    })]
  })
});
const MobileCategory = ({
  rowCount,
  seleniumId
}) => /*#__PURE__*/_jsxs(View, {
  "data-selenium-id": seleniumId,
  borderBlockStartStyle: "solid",
  borderWidth: "200",
  borderColor: "border-core-default",
  children: [/*#__PURE__*/_jsx(View, {
    paddingBlock: "400",
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      paddingInline: "200",
      children: [/*#__PURE__*/_jsx(SkeletonBar, {
        $height: 20,
        $width: 160
      }), /*#__PURE__*/_jsx(SkeletonBar, {
        $height: 20,
        $width: 20
      })]
    })
  }), Array.from({
    length: rowCount
  }, (_, index) => /*#__PURE__*/_jsx(MobileFeatureRow, {}, index))]
});
const MobileSkeleton = ({
  columnCount
}) => /*#__PURE__*/_jsxs(SkeletonMobileLayout, {
  "data-selenium-id": "hsoc-feature-table-skeleton-mobile",
  children: [/*#__PURE__*/_jsx(Title, {}), /*#__PURE__*/_jsx(Flex, {
    gap: "200",
    paddingInline: "400",
    paddingBlockStart: "600",
    paddingBlockEnd: "400",
    children: Array.from({
      length: columnCount
    }, (_, index) => /*#__PURE__*/_jsx(View, {
      "data-selenium-id": "hsoc-feature-table-skeleton-tab",
      flex: 1,
      children: /*#__PURE__*/_jsx(SkeletonBar, {
        $height: 40,
        $width: "100%"
      })
    }, index))
  }), /*#__PURE__*/_jsx(View, {
    paddingInline: "400",
    paddingBlockEnd: "400",
    children: /*#__PURE__*/_jsx(SkeletonBar, {
      $height: 40,
      $width: "100%"
    })
  }), /*#__PURE__*/_jsx(View, {
    paddingInline: "400",
    paddingBlockEnd: "400",
    children: /*#__PURE__*/_jsx(SkeletonBar, {
      $height: 40,
      $width: "100%"
    })
  }), /*#__PURE__*/_jsxs(View, {
    paddingInline: "400",
    paddingBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(MobileCategory, {
      rowCount: PLAN_DETAILS_ROW_COUNT,
      seleniumId: "hsoc-feature-table-skeleton-plan-details"
    }), Array.from({
      length: SKELETON_CATEGORY_COUNT
    }, (_, index) => /*#__PURE__*/_jsx(MobileCategory, {
      rowCount: SKELETON_ROWS_PER_CATEGORY,
      seleniumId: "hsoc-feature-table-skeleton-category"
    }, index))]
  })]
});

/*
 * Full-height loading placeholder for HsocFeatureTable. Mirrors the real table's
 * header, the Plan Details group, and the expanded feature categories + catalog
 * link (desktop + mobile) so the page reserves the table's space while categories
 * load, instead of snapping from a short spinner to the full table and shoving
 * the content below it down (CLS). Exposes a single loading status region rather
 * than one per bar.
 */
const HsocFeatureTableSkeleton = ({
  columnCount
}) => /*#__PURE__*/_jsxs("div", {
  "data-selenium-id": "hsoc-feature-table-skeleton",
  role: "status",
  "aria-busy": true,
  "aria-label": unescapedText('hsocPricingPage.featureTable.loading'),
  children: [/*#__PURE__*/_jsx(DesktopSkeleton, {
    columnCount: columnCount
  }), /*#__PURE__*/_jsx(MobileSkeleton, {
    columnCount: columnCount
  })]
});
export default HsocFeatureTableSkeleton;